import { Box, FormControlLabel, Radio as MuiRadio, RadioProps, Stack, SxProps, Theme, Typography, css, styled } from "@mui/material";
import { ChangeEvent, FC, ReactNode } from "react";

import { useFormField } from "@/modules/irohana-components/elements/FormField/FormField";
import { borderColor, button, state, text } from "@/modules/irohana-components/theme/colors-new";

export type TRadioNewProps = {
  label?: ReactNode;
  subLabel?: string;
  nonControl?: boolean;
  prefixLabel?: ReactNode;
  error?: boolean;
  readOnly?: boolean;
  labelColor?: string;
  subLabelColor?: string;
  alignStart?: boolean;
  labelSx?: SxProps<Theme>;
  formControlLabelSx?: SxProps<Theme>;
} & RadioProps;

export const RadioNew: FC<TRadioNewProps> = ({
  label,
  subLabel,
  nonControl = false,
  prefixLabel,
  error,
  readOnly = false,
  labelColor = text.primary,
  subLabelColor = text.secondary,
  alignStart = true,
  labelSx,
  formControlLabelSx,
  size = "small",
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    field?.onChange(e);
    field?.onBlur();
    rest.onChange && rest.onChange(e, e.target.checked);
  };

  const radioSize = size === "small" ? 20 : 26;
  const iconSize = size === "small" ? 20 : 24;
  const outlineOffset = size === "small" ? 2 : 1;

  return (
    <RadioWrap alignStart={alignStart}>
      <FormControlLabel
        sx={formControlLabelSx}
        label={
          <Stack flexDirection="row" gap={0.5}>
            {prefixLabel && (
              <Typography variant="body14" color={text.primary}>
                {prefixLabel}
              </Typography>
            )}
            <Stack gap={0.5}>
              {label && (
                <Typography variant="body14" sx={labelSx} color={labelColor}>
                  {label}
                </Typography>
              )}
              {subLabel && (
                <Typography variant="cap12" color={subLabelColor}>
                  {subLabel}
                </Typography>
              )}
            </Stack>
          </Stack>
        }
        control={
          <StyledRadio
            error={Boolean(error) || Boolean(fieldState?.error)}
            readOnly={readOnly}
            radioSize={radioSize}
            iconSize={iconSize}
            outlineOffset={outlineOffset}
            size={size}
            {...rest}
            {...field}
            onChange={readOnly ? undefined : handleChange}
          />
        }
      />
    </RadioWrap>
  );
};

const RadioWrap = styled(Box)<{ alignStart: boolean }>`
  .MuiFormControlLabel-root {
    align-items: ${({ alignStart }) => (alignStart ? "flex-start !important" : "center !important")};
    gap: 12px;
    margin: 0;
  }
`;

const options = { shouldForwardProp: (propName: string) => !["error", "readOnly", "radioSize", "iconSize", "outlineOffset"].includes(propName) };

const StyledRadio = styled(MuiRadio, options)<{ error: boolean; readOnly: boolean; radioSize: number; iconSize: number; outlineOffset: number }>`
  width: ${({ radioSize }) => radioSize}px;
  height: ${({ radioSize }) => radioSize}px;
  position: relative;
  .MuiSvgIcon-root {
    font-size: ${({ iconSize }) => iconSize}px;
  }
  &::after {
    content: "";
    position: absolute;
    top: -${({ outlineOffset }) => outlineOffset}px;
    left: -${({ outlineOffset }) => outlineOffset}px;
    right: -${({ outlineOffset }) => outlineOffset}px;
    bottom: -${({ outlineOffset }) => outlineOffset}px;
    border: 2px solid transparent;
    border-radius: 50%;
    pointer-events: none;
  }
  &:focus-visible::after,
  &.Mui-focusVisible::after {
    border-color: ${borderColor.orange};
  }
  &.Mui-focusVisible {
    .MuiTouchRipple-root {
      display: none;
    }
  }
  ${({ error }) =>
    error
      ? css`
          color: ${state.error_1}!important;
        `
      : css`
          color: ${button.primary}!important;
        `};
  &.Mui-checked .MuiSvgIcon-root:nth-of-type(2) {
    transform: scale(1.2);
  }
  &.Mui-disabled {
    color: ${button.disable}!important;
  }
  ${({ readOnly }) =>
    readOnly &&
    css`
      color: #abb4b6 !important;
      pointer-events: none;
    `};
`;
